import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Grid,
  TextField,
  Button,
  Typography,
  Divider,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  Alert,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  FormControlLabel,
  Radio,
  RadioGroup,
  Card, CardContent, Checkbox
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Search as SearchIcon,
  ArrowRightAlt as ArrowIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { TimePicker } from '@mui/x-date-pickers';
import { cateringService } from '../../services/cateringService';
import {
  save_catering_quotations,
  get_specific_quotation,
  update_catering_quotation,
  create_catering_invoice,
  get_all_quotations
} from '../../services/cateringinfoservice';
import { useNotification } from '../../contexts/NotificationContext';
import { useLocation } from "react-router-dom";
import { useNavigate } from 'react-router-dom';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { useRestaurant } from '../../contexts/RestaurantContext';
import outletService from '../../services/outletService';

// Choice Selection Modal Component (reused from Quotation)
const ChoiceSelectionModal = ({
  open,
  onClose,
  onSave,
  item,
  mainItemName
}) => {
  const [selectedChoice, setSelectedChoice] = useState('');

  useEffect(() => {
    if (open && item) {
      setSelectedChoice('');
    }
  }, [open, item]);

  const handleSave = () => {
    if (selectedChoice && item) {
      onSave({
        ...item,
        selectedChoice: selectedChoice,
        name: item.name || mainItemName,
        displayName: selectedChoice === item.name ?
          item.name :
          `${item.name} (${selectedChoice})`,
        choice: true,
        other_choices: item.other_choices || [],
        selectedChoice: selectedChoice
      });
    }
    onClose();
  };

  if (!item || !item.other_choices || item.other_choices.length === 0) {
    return null;
  }

  const allOptions = [item.name, ...(item.other_choices || [])];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Select Choice for {item.name}</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Paper sx={{ p: 2, mb: 3, bgcolor: 'primary.light', color: 'white' }}>
          <Typography variant="subtitle1" fontWeight="bold">
            {item.name}
          </Typography>
          {item.description && (
            <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
              {item.description}
            </Typography>
          )}
        </Paper>

        <Typography variant="body2" color="text.secondary" gutterBottom>
          Please select one of the following options:
        </Typography>

        <RadioGroup
          value={selectedChoice}
          onChange={(e) => setSelectedChoice(e.target.value)}
          sx={{ mt: 2 }}
        >
          {allOptions.map((option, index) => (
            <FormControlLabel
              key={index}
              value={option}
              control={<Radio />}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="body1">
                    {option === item.name ? (
                      <Box component="span" sx={{ fontWeight: 'bold' }}>
                        {option} (Original)
                      </Box>
                    ) : (
                      option
                    )}
                  </Typography>
                </Box>
              }
              sx={{
                p: 2,
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                mb: 1,
                width: '100%',
                mx: 0,
                bgcolor: option === item.name ? 'grey.50' : 'white',
                '&:hover': {
                  bgcolor: 'action.hover',
                  borderColor: 'primary.main'
                },
                '& .MuiFormControlLabel-label': {
                  width: '100%'
                }
              }}
            />
          ))}
        </RadioGroup>

        {selectedChoice && (
          <Paper sx={{ p: 2, mt: 3, bgcolor: 'success.light', color: 'white' }}>
            <Typography variant="body2">
              Selected: <strong>
                {selectedChoice === item.name ?
                  item.name :
                  `${item.name} (${selectedChoice})`
                }
              </strong>
            </Typography>
          </Paper>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!selectedChoice}
          startIcon={<SaveIcon />}
        >
          Confirm Choice
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Menu Selection Modal - For Invoice Component
const MenuSelectionModal = ({
  open,
  onClose,
  onSave,
  initialData = null,
  mainMenuIds,
  setMainMenuIds,
  menuItemIds,
  setMenuItemIds,
  setMenuPrice,
  setMenuQuantity,
  isEditing = false,
  editingMenuId = null,
  allMenuItems = [],
  mainMenus = [],
  choiceItems,
  setChoiceItems,
  isViewOnly = false
}) => {
  const [selectedCategory, setSelectedCategory] = useState('');
  const [searchLeft, setSearchLeft] = useState('');
  const [searchRight, setSearchRight] = useState('');
  const [selectedItems, setSelectedItems] = useState([]);
  const [quantity, setQuantity] = useState('1');
  const [price, setPrice] = useState('');
  const [previousCategoryId, setPreviousCategoryId] = useState(null);
  const [choiceModalOpen, setChoiceModalOpen] = useState(false);
  const [pendingItem, setPendingItem] = useState(null);
  const [pendingItemType, setPendingItemType] = useState(null);

  // Ref to track manually removed item IDs
  const removedItemIds = React.useRef(new Set());

  // Ref to track initial mount
  const isInitialMount = React.useRef(true);

  // Reset removed items tracking when modal opens
  useEffect(() => {
    if (open) {
      removedItemIds.current = new Set();
    }
  }, [open]);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setSelectedCategory('');
      setPreviousCategoryId(null);
      setSelectedItems([]);
      setQuantity('1');
      setPrice('');
      setSearchLeft('');
      setSearchRight('');
      setChoiceModalOpen(false);
      setPendingItem(null);
      setPendingItemType(null);
      isInitialMount.current = true;
    }
  }, [open]);

  // Main editing useEffect
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      if (!isEditing) return;
    }

    if (initialData && isEditing && open) {
      // console.log("Editing initialData:", initialData);

      const mainMenu = initialData.main_menu || initialData;
      const categoryId = mainMenu?.id || mainMenu?.categoryId || '';
      setSelectedCategory(categoryId);
      setPreviousCategoryId(categoryId);

      const categoryChoiceItems = Array.isArray(choiceItems[categoryId]) ? choiceItems[categoryId] : [];
      // console.log("Category Choice Items:", categoryChoiceItems);

      let menuItems = [];
      if (mainMenu.menuItems && Array.isArray(mainMenu.menuItems)) {
        menuItems = mainMenu.menuItems;
      } else if (mainMenu.menu_items && Array.isArray(mainMenu.menu_items)) {
        menuItems = mainMenu.menu_items;
      } else if (initialData.menuItems && Array.isArray(initialData.menuItems)) {
        menuItems = initialData.menuItems;
      }

      // console.log("Menu items to set:", menuItems);

      const transformedExistingItems = menuItems.map(item => {
        const itemId = item.catering_item?.id || item.id || item.catering_item_id;
        const choiceItem = categoryChoiceItems.find(ci => String(ci.id) === String(itemId));
        const selectedChoice = choiceItem?.selected || item.selectedChoice || null;

        let baseName = '';
        let baseDescription = '';
        let otherChoices = [];
        let isChoice = false;

        if (item.catering_item) {
          baseName = item.catering_item.name;
          baseDescription = item.catering_item.description || '';
          otherChoices = item.catering_item.other_choices || [];
          isChoice = item.catering_item.choice || false;
        } else {
          baseName = item.name || '';
          baseDescription = item.description || '';
          otherChoices = item.other_choices || [];
          isChoice = item.choice || false;
        }

        let displayName = baseName;
        if (selectedChoice) {
          displayName = selectedChoice === baseName
            ? baseName
            : `${baseName} (${selectedChoice})`;
        }

        return {
          id: itemId,
          name: baseName,
          description: baseDescription,
          choice: isChoice,
          other_choices: otherChoices,
          selectedChoice: selectedChoice,
          displayName: displayName,
          fromCategory: categoryId,
          catering_item_id: itemId,
          catering_item: {
            id: itemId,
            name: baseName,
            description: baseDescription,
            choice: isChoice,
            other_choices: otherChoices
          }
        };
      }).filter(item => item.id);

      // console.log("Transformed existing items:", transformedExistingItems);

      const existingItemIds = new Set(
        transformedExistingItems.map(item => String(item.id))
      );

      const orphanedChoiceItems = categoryChoiceItems.filter(
        choiceItem => !existingItemIds.has(String(choiceItem.id))
      );

      // console.log("Orphaned choice items:", orphanedChoiceItems);

      const orphanedItems = orphanedChoiceItems.map(choiceItem => {
        const fullItem = allMenuItems.find(item => String(item.id) === String(choiceItem.id));

        if (fullItem) {
          return {
            id: fullItem.id,
            name: fullItem.name,
            description: fullItem.description || '',
            choice: fullItem.choice || false,
            other_choices: fullItem.other_choices || [],
            selectedChoice: choiceItem.selected,
            displayName: `${fullItem.name} (${choiceItem.selected})`,
            fromCategory: categoryId,
            catering_item_id: fullItem.id,
            catering_item: {
              id: fullItem.id,
              name: fullItem.name,
              description: fullItem.description || '',
              choice: fullItem.choice || false,
              other_choices: fullItem.other_choices || []
            }
          };
        } else {
          return {
            id: choiceItem.id,
            name: `Item ${choiceItem.id}`,
            description: '',
            choice: true,
            other_choices: [],
            selectedChoice: choiceItem.selected,
            displayName: `Item ${choiceItem.id} (${choiceItem.selected})`,
            fromCategory: categoryId,
            catering_item_id: choiceItem.id,
            catering_item: {
              id: choiceItem.id,
              name: `Item ${choiceItem.id}`,
              description: '',
              choice: true,
              other_choices: []
            }
          };
        }
      });

      // console.log("Created orphaned items:", orphanedItems);

      // Combine and filter out manually removed items
      const allItems = [...transformedExistingItems, ...orphanedItems]
        .filter(item => !removedItemIds.current.has(String(item.id)));

      // console.log("Final combined items (after removed filter):", allItems);
      setSelectedItems(allItems);

      const menuQuantity = mainMenu.quantity || initialData.quantity || 1;
      const menuPrice = mainMenu.price_per_person || mainMenu.price || initialData.price_per_person || initialData.price || 0;
      setQuantity(menuQuantity.toString());
      setPrice(menuPrice.toString());
    }
  }, [initialData, isEditing, open, choiceItems, allMenuItems]);

  // Sync choice selections useEffect
  useEffect(() => {
    if (isEditing && open && selectedCategory && choiceItems[selectedCategory]) {
      const updatedChoiceItems = Array.isArray(choiceItems[selectedCategory])
        ? choiceItems[selectedCategory]
        : [];

      if (updatedChoiceItems.length > 0) {
        setSelectedItems(prevItems => {
          return prevItems
            .filter(item => {
              const id = item.id || item.catering_item?.id;
              return !removedItemIds.current.has(String(id));
            })
            .map(item => {
              const itemId = item.id || item.catering_item?.id;
              const choiceItem = updatedChoiceItems.find(
                ci => String(ci.id) === String(itemId)
              );

              if (choiceItem && choiceItem.selected) {
                const baseName = item.name || item.catering_item?.name;
                const displayName = choiceItem.selected === baseName
                  ? baseName
                  : `${baseName} (${choiceItem.selected})`;

                return {
                  ...item,
                  selectedChoice: choiceItem.selected,
                  displayName: displayName
                };
              }
              return item;
            });
        });
      }
    }
  }, [choiceItems, selectedCategory, isEditing, open]);

  const getCategoryItems = () => {
    if (!selectedCategory) return [];
    const category = mainMenus.find(cat => cat.id === selectedCategory);
    return category ? category.menu_items : [];
  };

  const getLeftItems = () => {
    const items = getCategoryItems();
    const selectedIds = selectedItems
      .map(item => item.id || item.catering_item?.id)
      .filter(id => id);

    return items.filter(item =>
      item &&
      item.catering_item &&
      item.catering_item.id &&
      !selectedIds.includes(item.catering_item.id) &&
      item.catering_item.name &&
      item.catering_item.name.toLowerCase().includes(searchLeft.toLowerCase())
    );
  };

  const getRightItems = () => {
    const selectedIdSet = new Set(
      selectedItems
        .map(item => {
          if (item.catering_item && item.catering_item.id) {
            return item.catering_item.id;
          }
          return item.id || item.catering_item_id;
        })
        .filter(id => id)
    );

    return allMenuItems.filter(item =>
      item &&
      item.id &&
      !selectedIdSet.has(item.id) &&
      item.name &&
      item.name.toLowerCase().includes(searchRight.toLowerCase())
    );
  };

  const handleSelectLeftItem = (item) => {
    if (isViewOnly || !item || !item.catering_item || !item.catering_item.id) return;

    const itemId = item.catering_item.id;
    const isAlreadySelected = selectedItems.some(selectedItem =>
      (selectedItem.catering_item && selectedItem.catering_item.id === itemId) ||
      selectedItem.id === itemId ||
      selectedItem.catering_item_id === itemId
    );

    if (!isAlreadySelected) {
      // Remove from removedItemIds if user re-adds it
      removedItemIds.current.delete(String(itemId));

      if (item.catering_item.choice &&
        item.catering_item.other_choices &&
        item.catering_item.other_choices.length > 0) {

        setPendingItem({
          id: itemId,
          name: item.catering_item.name,
          description: item.catering_item.description,
          choice: item.catering_item.choice,
          other_choices: item.catering_item.other_choices || [],
          originalItem: item.catering_item,
          fromCategory: selectedCategory
        });
        setPendingItemType('left');
        setChoiceModalOpen(true);
      } else {
        const newItem = {
          ...item,
          id: itemId,
          name: item.catering_item.name,
          description: item.catering_item.description,
          choice: false,
          other_choices: [],
          displayName: item.catering_item.name,
          fromCategory: selectedCategory,
          catering_item_id: itemId,
          catering_item: {
            ...item.catering_item,
            id: itemId,
            name: item.catering_item.name,
            description: item.catering_item.description,
            choice: false,
            other_choices: []
          }
        };
        setSelectedItems(prev => [...prev, newItem]);
      }
    }
  };

  const handleSelectRightItem = (item) => {
    if (isViewOnly || !item || !item.id) return;

    const isAlreadySelected = selectedItems.some(selectedItem =>
      (selectedItem.catering_item && selectedItem.catering_item.id === item.id) ||
      selectedItem.id === item.id ||
      selectedItem.catering_item_id === item.id
    );

    if (!isAlreadySelected) {
      // Remove from removedItemIds if user re-adds it
      removedItemIds.current.delete(String(item.id));

      if (item.choice &&
        item.other_choices &&
        item.other_choices.length > 0) {

        setPendingItem({
          id: item.id,
          name: item.name,
          description: item.description,
          choice: item.choice,
          other_choices: item.other_choices || [],
          originalItem: item,
          fromCategory: 'other'
        });
        setPendingItemType('right');
        setChoiceModalOpen(true);
      } else {
        const newItem = {
          ...item,
          id: item.id,
          name: item.name,
          description: item.description,
          choice: false,
          other_choices: [],
          displayName: item.name,
          catering_item_id: item.id,
          fromCategory: 'other',
          catering_item: {
            ...item,
            id: item.id,
            name: item.name,
            description: item.description,
            choice: false,
            other_choices: []
          }
        };
        setSelectedItems(prev => [...prev, newItem]);
      }
    }
  };

  const handleChoiceSave = (itemWithChoice) => {
    if (isViewOnly) return;

    // console.log("Saving choice item:", itemWithChoice);

    const currentCategory = selectedCategory;
    const categoryKey = currentCategory;

    const itemToAdd = {
      id: itemWithChoice.id,
      name: itemWithChoice.name,
      description: itemWithChoice.description,
      choice: true,
      other_choices: itemWithChoice.other_choices,
      selectedChoice: itemWithChoice.selectedChoice,
      displayName: itemWithChoice.displayName,
      originalItem: itemWithChoice.originalItem,
      fromCategory: categoryKey,
      catering_item_id: itemWithChoice.id,
      catering_item: {
        id: itemWithChoice.id,
        name: itemWithChoice.name,
        description: itemWithChoice.description,
        choice: true,
        other_choices: itemWithChoice.other_choices
      }
    };

    setChoiceItems(prevItems => {
      const currentCategoryItems = prevItems[categoryKey] || [];
      const safeCategoryItems = Array.isArray(currentCategoryItems) ? currentCategoryItems : [];
      const existsIndex = safeCategoryItems.findIndex(item => item.id === itemToAdd.id);

      let updatedCategoryItems;
      if (existsIndex !== -1) {
        updatedCategoryItems = [...safeCategoryItems];
        updatedCategoryItems[existsIndex] = {
          id: itemToAdd.id,
          selected: itemWithChoice.selectedChoice
        };
      } else {
        updatedCategoryItems = [...safeCategoryItems, {
          id: itemToAdd.id,
          selected: itemWithChoice.selectedChoice
        }];
      }

      return { ...prevItems, [categoryKey]: updatedCategoryItems };
    });

    setSelectedItems(prevItems => {
      const existsIndex = prevItems.findIndex(item =>
        item.id === itemToAdd.id ||
        (item.catering_item && item.catering_item.id === itemToAdd.id) ||
        item.catering_item_id === itemToAdd.id
      );

      if (existsIndex !== -1) {
        const updatedItems = [...prevItems];
        updatedItems[existsIndex] = {
          ...itemToAdd,
          fromCategory: categoryKey
        };
        return updatedItems;
      } else {
        return [...prevItems, {
          ...itemToAdd,
          fromCategory: categoryKey
        }];
      }
    });

    setChoiceModalOpen(false);
    setPendingItem(null);
    setPendingItemType(null);
  };

  const handleRemoveItem = (itemId) => {
    if (isViewOnly || !itemId) return;

    // Track this removal so useEffect won't re-add it
    removedItemIds.current.add(String(itemId));

    // Find item with flexible ID matching
    const itemToRemove = selectedItems.find(item => {
      const ids = [
        item.id,
        item.catering_item?.id,
        item.catering_item_id
      ].filter(Boolean);
      return ids.some(id => String(id) === String(itemId));
    });

    if (!itemToRemove) return;

    const categoryId = itemToRemove.fromCategory;

    // Remove from selectedItems
    setSelectedItems(prevItems =>
      prevItems.filter(item => {
        const ids = [
          item.id,
          item.catering_item?.id,
          item.catering_item_id
        ].filter(Boolean);
        return !ids.some(id => String(id) === String(itemId));
      })
    );

    // Remove from choiceItems
    if (categoryId) {
      setChoiceItems(prevItems => {
        const categoryItems = prevItems[categoryId];
        const safeCategoryItems = Array.isArray(categoryItems) ? categoryItems : [];

        return {
          ...prevItems,
          [categoryId]: safeCategoryItems.filter(
            item => String(item.id) !== String(itemId)
          )
        };
      });
    }
  };

  const handleSave = () => {
    if (isViewOnly) return;

    const numericQuantity = quantity === '' || quantity === '.' || quantity === '-' ? 1 : parseFloat(quantity) || 1;
    const numericPrice = price === '' || price === '.' || price === '-' ? 0 : parseFloat(price) || 0;

    const menuData = {
      id: selectedCategory,
      name: mainMenus.find(c => c.id === selectedCategory)?.name || 'Mixed Items',
      menuItems: selectedItems.map(item => ({
        ...item,
        id: item.id || item.catering_item?.id,
        name: item.name || item.catering_item?.name || 'Unnamed Item',
        description: item.description || item.catering_item?.description || '',
        choice: item.choice || item.catering_item?.choice || false,
        other_choices: item.other_choices || item.catering_item?.other_choices || [],
        selectedChoice: item.selectedChoice,
        displayName: item.displayName || item.name || item.catering_item?.name
      })),
      quantity: numericQuantity,
      price_per_person: numericPrice,
      total: numericPrice * numericQuantity,
      createdAt: initialData?.createdAt || new Date().toISOString(),
      method: 'manual'
    };

    if (isEditing && previousCategoryId) {
      setMainMenuIds(prev => {
        const newMainMenuIds = prev.filter(id => id !== previousCategoryId);
        return [...newMainMenuIds, selectedCategory];
      });

      setMenuItemIds(prevState => {
        const newState = { ...prevState };
        delete newState[previousCategoryId];
        const selectedItemIds = selectedItems
          .map(item => item.id || item.catering_item?.id)
          .filter(id => id !== undefined && id !== null);
        newState[selectedCategory] = selectedItemIds;
        return newState;
      });

      setMenuPrice(prev => {
        const newState = { ...prev };
        delete newState[previousCategoryId];
        newState[selectedCategory] = numericPrice;
        return newState;
      });

      setMenuQuantity(prev => {
        const newState = { ...prev };
        delete newState[previousCategoryId];
        newState[selectedCategory] = numericQuantity;
        return newState;
      });
    } else {
      setMainMenuIds(prev => [...prev, selectedCategory]);

      const selectedItemIds = selectedItems
        .map(item => item.id || item.catering_item?.id)
        .filter(id => id !== undefined && id !== null);

      setMenuItemIds(prevState => {
        const newState = { ...prevState };
        if (newState[selectedCategory]) {
          newState[selectedCategory] = [...newState[selectedCategory], ...selectedItemIds];
        } else {
          newState[selectedCategory] = selectedItemIds;
        }
        return newState;
      });

      setMenuPrice(prev => ({ ...prev, [selectedCategory]: numericPrice }));
      setMenuQuantity(prev => ({ ...prev, [selectedCategory]: numericQuantity }));
    }

    onClose();
    onSave(menuData);
  };

  const numericQuantity = quantity === '' || quantity === '.' || quantity === '-' ? 1 : parseFloat(quantity) || 1;
  const numericPrice = price === '' || price === '.' || price === '-' ? 0 : parseFloat(price) || 0;
  const totalAmount = numericPrice * numericQuantity;

  const getDisplayName = (item) => {
    if (item.displayName) return item.displayName;
    if (item.selectedChoice) {
      const baseName = item.name || item.catering_item?.name || 'Unnamed Item';
      return item.selectedChoice === baseName
        ? baseName
        : `${baseName} (${item.selectedChoice})`;
    }
    if (item.catering_item) return item.catering_item.name || 'Unnamed Item';
    return item.name || 'Unnamed Item';
  };

  const handleNumberInput = (value, setter) => {
    if (isViewOnly) return;

    if (value === '') {
      setter('');
      return;
    }

    const decimalRegex = /^-?\d*\.?\d*$/;
    if (decimalRegex.test(value)) {
      if (value === '.' || value === '-.' || value.startsWith('..')) return;
      const decimalCount = (value.match(/\./g) || []).length;
      if (decimalCount > 1) return;
      if (value.includes('-') && !value.startsWith('-')) return;
      setter(value);
    }
  };

  const handleNumberBlur = (value, setter) => {
    if (isViewOnly) return;

    if (value === '' || value === '-' || value === '.' || value === '-.') {
      setter('');
    } else if (value.endsWith('.') || value.endsWith('.0')) {
      const numericValue = parseFloat(value);
      if (!isNaN(numericValue)) setter(numericValue.toString());
    }
  };

  const isQuantityValid = quantity !== '' && quantity !== '.' && quantity !== '-' && !isNaN(parseFloat(quantity));
  const isPriceValid = price !== '' && price !== '.' && price !== '-' && !isNaN(parseFloat(price));

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { maxHeight: '85vh' } }}
      >
        <DialogTitle sx={{ pb: 1, borderBottom: 1, borderColor: 'divider' }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              {isEditing ? 'Edit Menu Selection' : 'Add Menu Selection'}
              {isViewOnly && ' (View Only)'}
            </Typography>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 3 }}>
          <Grid container spacing={3}>
            {/* Top Section */}
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={5}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Select Main Menu Category</InputLabel>
                      <Select
                        value={selectedCategory}
                        onChange={(e) => {
                          if (isViewOnly) return;
                          const newCategoryId = e.target.value;
                          setSelectedCategory(newCategoryId);

                          const selectedMenu = mainMenus.find(menu => menu.id === newCategoryId);
                          if (selectedMenu) {
                            setPrice(selectedMenu.price_per_person);
                          }

                          if (!isEditing || previousCategoryId !== newCategoryId) {
                            setSelectedItems([]);
                            // Reset removed tracking on category change
                            removedItemIds.current = new Set();
                          }

                          const menuItems = mainMenus.find(menu => menu.id === newCategoryId);
                          if (menuItems && menuItems.menu_items && !isEditing) {
                            const transformedItems = menuItems.menu_items.map(item => ({
                              ...item,
                              id: item.catering_item?.id,
                              name: item.catering_item?.name,
                              description: item.catering_item?.description,
                              choice: item.catering_item?.choice || false,
                              other_choices: item.catering_item?.other_choices || [],
                              fromCategory: newCategoryId,
                              catering_item_id: item.catering_item?.id,
                              catering_item: {
                                ...item.catering_item,
                                id: item.catering_item?.id,
                                name: item.catering_item?.name,
                                description: item.catering_item?.description,
                                choice: item.catering_item?.choice,
                                other_choices: item.catering_item?.other_choices
                              }
                            })).filter(item => item.id);
                            setSelectedItems(transformedItems);
                          }
                          setSearchLeft('');
                          setSearchRight('');
                        }}
                        label="Select Main Menu Category"
                        disabled={isViewOnly}
                      >
                        <MenuItem value=""><em>Select a category</em></MenuItem>
                        {mainMenus.map(category => (
                          <MenuItem
                            key={category.id}
                            value={category.id}
                            disabled={mainMenuIds.includes(category.id) && !isEditing}
                          >
                            {category.name}
                            {mainMenuIds.includes(category.id) && (
                              <Chip
                                sx={{ ml: 1, opacity: 1 }}
                                color="success"
                                label="Selected"
                                size="small"
                              />
                            )}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={2}>
                    <TextField
                      fullWidth
                      label="Quantity"
                      type="text"
                      value={quantity}
                      onChange={(e) => handleNumberInput(e.target.value, setQuantity)}
                      onBlur={(e) => handleNumberBlur(e.target.value, setQuantity)}
                      size="small"
                      error={quantity !== '' && !isQuantityValid}
                      helperText={quantity !== '' && !isQuantityValid ? "Please enter a valid number" : ""}
                      disabled={isViewOnly}
                    />
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label="Price per item"
                      type="text"
                      value={price}
                      onChange={(e) => handleNumberInput(e.target.value, setPrice)}
                      onBlur={(e) => handleNumberBlur(e.target.value, setPrice)}
                      size="small"
                      InputProps={{
                        startAdornment: <InputAdornment position="start">S$</InputAdornment>,
                      }}
                      error={price !== '' && !isPriceValid}
                      helperText={price !== '' && !isPriceValid ? "Please enter a valid number" : ""}
                      disabled={isViewOnly}
                    />
                  </Grid>

                  <Grid item xs={12} md={2}>
                    <Box sx={{ p: 1, bgcolor: 'primary.light', borderRadius: 1, textAlign: 'center' }}>
                      <Typography variant="body2" fontWeight="bold" color="white">
                        S${totalAmount.toFixed(2)}
                      </Typography>
                      <Typography variant="caption" color="white">Total</Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* Selected Items Display */}
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="subtitle2" color="primary" fontWeight="medium">
                    Selected Menu Items
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {selectedItems.length} items selected
                  </Typography>
                </Box>

                {selectedItems.length === 0 ? (
                  <Box sx={{
                    p: 4,
                    border: '2px dashed #e0e0e0',
                    borderRadius: 1,
                    textAlign: 'center',
                    bgcolor: 'grey.50'
                  }}>
                    <Typography variant="body2" color="text.secondary">
                      No items selected yet. Select items from the boxes below.
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{
                    p: 2,
                    border: '1px solid #e0e0e0',
                    borderRadius: 1,
                    minHeight: 80,
                    bgcolor: 'grey.50'
                  }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {selectedItems.map(item => {
                        const itemId = item.id ?? item.catering_item?.id ?? item.catering_item_id;
                        const displayName = getDisplayName(item);

                        return (
                          <Chip
                            key={itemId}
                            label={displayName}
                            onDelete={isViewOnly ? undefined : () => handleRemoveItem(itemId)}
                            color={item.fromCategory === selectedCategory ? "primary" : "secondary"}
                            variant="outlined"
                            size="small"
                            sx={{
                              '& .MuiChip-deleteIcon': {
                                fontSize: '16px',
                                '&:hover': { color: 'error.main' },
                                display: isViewOnly ? 'none' : 'inline-flex'
                              }
                            }}
                          />
                        );
                      })}
                    </Box>
                  </Box>
                )}
              </Paper>
            </Grid>

            {/* Two Column Selection */}
            <Grid item xs={12}>
              <Grid container spacing={2}>
                {/* Left Column */}
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                    <Typography variant="subtitle2" gutterBottom fontWeight="medium">
                      {selectedCategory
                        ? `${mainMenus.find(c => c.id === selectedCategory)?.name || 'Category'} Items`
                        : 'Select a Category'
                      }
                    </Typography>

                    <TextField
                      fullWidth
                      placeholder="Search items in this category..."
                      value={searchLeft}
                      onChange={(e) => setSearchLeft(e.target.value)}
                      size="small"
                      sx={{ mb: 2 }}
                      InputProps={{
                        startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                      }}
                      disabled={!selectedCategory || isViewOnly}
                    />

                    <Box sx={{
                      height: 200, overflow: 'auto', p: 1,
                      border: '1px solid #e0e0e0', borderRadius: 1, bgcolor: 'white'
                    }}>
                      {selectedCategory ? (
                        getLeftItems().length > 0 ? (
                          <Stack spacing={1}>
                            {getLeftItems().map(item => (
                              <Box
                                key={item.catering_item?.id}
                                sx={{
                                  p: 1.5, border: '1px solid #e0e0e0', borderRadius: 1,
                                  cursor: isViewOnly ? 'default' : 'pointer',
                                  '&:hover': isViewOnly ? {} : {
                                    borderColor: 'primary.main', bgcolor: 'primary.light',
                                    '& .item-name': { color: 'white' },
                                    '& .item-desc': { color: 'white' }
                                  },
                                  opacity: isViewOnly ? 0.7 : 1
                                }}
                                onClick={() => handleSelectLeftItem(item)}
                              >
                                <Box display="flex" justifyContent="space-between" alignItems="center">
                                  <Box flex={1}>
                                    <Typography variant="body2" fontWeight="medium" className="item-name">
                                      {item.catering_item?.name || 'Unnamed Item'}
                                      {item.catering_item?.choice && item.catering_item.other_choices?.length > 0 && (
                                        <Chip
                                          label={`${item.catering_item.other_choices.length + 1} options`}
                                          size="small" color="warning" variant="outlined"
                                          sx={{ ml: 1, height: 20, fontSize: '0.6rem' }}
                                        />
                                      )}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" className="item-desc" noWrap>
                                      {item.description || ''}
                                      {item.catering_item?.other_choices && item.catering_item.other_choices.length > 0 && (
                                        <Box component="span" sx={{ display: 'block', mt: 0.5 }}>
                                          Options: {item.catering_item.name} • {item.catering_item.other_choices.join(' • ')}
                                        </Box>
                                      )}
                                    </Typography>
                                  </Box>
                                  {!isViewOnly && <ArrowIcon color="action" sx={{ ml: 1 }} />}
                                </Box>
                              </Box>
                            ))}
                          </Stack>
                        ) : (
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                            <Typography variant="body2" color="text.secondary" align="center">
                              {searchLeft ? 'No matching items found' : 'All items selected'}
                            </Typography>
                          </Box>
                        )
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                          <Typography variant="body2" color="text.secondary" align="center">
                            Please select a category first
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Paper>
                </Grid>

                {/* Right Column */}
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                    <Typography variant="subtitle2" gutterBottom fontWeight="medium">
                      Other Menu Items
                    </Typography>

                    <TextField
                      fullWidth
                      placeholder="Search other items..."
                      value={searchRight}
                      onChange={(e) => setSearchRight(e.target.value)}
                      size="small"
                      sx={{ mb: 2 }}
                      InputProps={{
                        startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                      }}
                      disabled={isViewOnly}
                    />

                    <Box sx={{
                      height: 200, overflow: 'auto', p: 1,
                      border: '1px solid #e0e0e0', borderRadius: 1, bgcolor: 'white'
                    }}>
                      {getRightItems().length > 0 ? (
                        <Stack spacing={1}>
                          {getRightItems().map(item => (
                            <Box
                              key={item.id}
                              sx={{
                                p: 1.5, border: '1px solid #e0e0e0', borderRadius: 1,
                                cursor: isViewOnly ? 'default' : 'pointer',
                                '&:hover': isViewOnly ? {} : {
                                  borderColor: 'secondary.main', bgcolor: 'secondary.light',
                                  '& .item-name': { color: 'white' },
                                  '& .item-desc': { color: 'white' }
                                },
                                opacity: isViewOnly ? 0.7 : 1
                              }}
                              onClick={() => handleSelectRightItem(item)}
                            >
                              <Box display="flex" justifyContent="space-between" alignItems="center">
                                <Box flex={1}>
                                  <Typography variant="body2" fontWeight="medium" className="item-name">
                                    {item.name || 'Unnamed Item'}
                                    {item.choice && item.other_choices?.length > 0 && (
                                      <Chip
                                        label={`${item.other_choices.length + 1} options`}
                                        size="small" color="warning" variant="outlined"
                                        sx={{ ml: 1, height: 20, fontSize: '0.6rem' }}
                                      />
                                    )}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" className="item-desc" noWrap>
                                    {item.description || ''}
                                    {item.other_choices && item.other_choices.length > 0 && (
                                      <Box component="span" sx={{ display: 'block', mt: 0.5 }}>
                                        Options: {item.name} • {item.other_choices.join(' • ')}
                                      </Box>
                                    )}
                                  </Typography>
                                </Box>
                                {!isViewOnly && <ArrowIcon color="action" sx={{ ml: 1 }} />}
                              </Box>
                            </Box>
                          ))}
                        </Stack>
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                          <Typography variant="body2" color="text.secondary" align="center">
                            {searchRight ? 'No matching items found' : 'No other items available'}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </DialogContent>

        {!isViewOnly && (
          <DialogActions sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}>
            <Button onClick={onClose} color="inherit">
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={selectedItems.length === 0 || !selectedCategory || !isPriceValid || price <= 0}
            >
              {isEditing ? 'Update Selection' : 'Add to Invoice'}
            </Button>
          </DialogActions>
        )}
      </Dialog>

      <ChoiceSelectionModal
        open={choiceModalOpen}
        onClose={() => {
          setChoiceModalOpen(false);
          setPendingItem(null);
          setPendingItemType(null);
        }}
        onSave={handleChoiceSave}
        item={pendingItem}
        mainItemName={pendingItem?.name || ''}
      />
    </>
  );
};


const AddonAdditionComponent = ({ addOnsData, setAddOnsData, addOnPrices, setAddOnPrices, addOnTotal, setAddOnTotal }) => {
  const [addOnItems, setAddOnItems] = useState([]);
  const [checkedItems, setCheckedItems] = useState({});

  const fetchAddOnItems = async () => {
    try {
      const response = await cateringService.getaddonItems();
      // console.log("response => ", response);
      setAddOnItems(response.add_on_items || []);
    } catch (error) {
      // console.error(error?.message);
    }
  };

  useEffect(() => {
    fetchAddOnItems();
  }, []);

  // Initialize checked items based on addOnsData
  useEffect(() => {
    if (addOnItems.length > 0) {
      const initialCheckedState = {};
      addOnItems.forEach((item, index) => {
        if (addOnsData && addOnsData[item.id]) {
          initialCheckedState[index] = true;
        }
      });
      setCheckedItems(initialCheckedState);
    }
  }, [addOnItems, addOnsData]);

  // Recalculate total whenever addOnsData or addOnPrices change
  useEffect(() => {
    calculateTotal();
  }, [addOnsData, addOnPrices]);

  const calculateTotal = () => {
    let total = 0;

    Object.keys(addOnsData).forEach(id => {
      const item = addOnsData[id];
      const priceItem = addOnPrices[id];

      if (item && priceItem) {
        const quantity = parseInt(item.quantity) || 0;
        const price = parseFloat(priceItem.price_per_item) || 0;
        total += quantity * price;
      }
    });

    setAddOnTotal(total);
    // console.log("Total calculated:", total);
  };

  const handleCheck = (index, id) => {
    const isChecked = !checkedItems[index];

    // Update checkbox state
    setCheckedItems((prev) => ({
      ...prev,
      [index]: isChecked,
    }));

    if (isChecked) {
      // When checking, initialize with quantity 1
      setAddOnsData((prev) => ({
        ...prev,
        [id]: {
          name: addOnItems[index].name,
          quantity: 1
        }
      }));

      // Initialize price if not exists
      if (!addOnPrices[id]) {
        setAddOnPrices((prev) => ({
          ...prev,
          [id]: {
            price_per_item: 1
          }
        }));
      }
    } else {
      // When unchecking, remove the item from both states
      setAddOnsData((prev) => {
        const updated = { ...prev };
        delete updated[id];
        return updated;
      });

      setAddOnPrices((prev) => {
        const updated = { ...prev };
        delete updated[id];
        return updated;
      });
    }
  };

  const handleAddOnQuantityChange = (value, id, name) => {
    // console.log("ID and Value ", id, " ", value);

    // Handle empty string
    if (value === "") {
      setAddOnsData((prev) => ({
        ...prev,
        [id]: {
          ...prev[id],
          name: name,
          quantity: ""
        }
      }));
      return;
    }

    // Parse the value to a number
    let numValue = parseInt(value, 10);

    // Handle invalid input
    if (isNaN(numValue)) {
      return;
    }

    // Limit to max 5000
    if (numValue > 5000) {
      numValue = 5000;
    }

    // Don't allow zero
    if (numValue < 1) {
      numValue = 1;
    }

    setAddOnsData((prev) => ({
      ...prev,
      [id]: {
        name: name,
        quantity: numValue
      }
    }));
  };

  // const handleAddOnPriceChange = (value, id) => {
  //   console.log("Price ID and Value ", id, " ", value);

  //   // Handle empty string
  //   if (value === "") {
  //     setAddOnPrices((prev) => ({
  //       ...prev,
  //       [id]: {
  //         price_per_item: 0
  //       }
  //     }));
  //     return;
  //   }

  //   // Use parseFloat for decimal support
  //   let numValue = parseFloat(value);

  //   // Handle invalid input
  //   if (isNaN(numValue)) {
  //     return;
  //   }

  //   // Limit to max 5000
  //   if (numValue > 5000) {
  //     numValue = 5000;
  //   }

  //   // Don't allow negative
  //   if (numValue < 0) {
  //     numValue = 0;
  //   }

  //   setAddOnPrices((prev) => ({
  //     ...prev,
  //     [id]: {
  //       price_per_item: numValue
  //     }
  //   }));
  // };


  const handleAddOnPriceChange = (value, id) => {
    // console.log("Price ID and Value ", id, " ", value);

    // Handle empty string
    if (value === "") {
      setAddOnsData((prev) => ({
        ...prev,
        [id]: {
          ...prev[id],
          price: ""
        }
      }));

      setAddOnPrices((prev) => ({
        ...prev,
        [id]: {
          price_per_item: 0
        }
      }));
      return;
    }

    // Parse the value for validation, but keep the original string for display
    let numValue = parseFloat(value);

    // Handle invalid input - if it's a partial number like "12." or "0."
    // parseFloat will still work, but we need to allow typing
    if (isNaN(numValue)) {
      // If it's a valid decimal pattern like "12." or ".5", allow it
      const decimalPattern = /^\d*\.?\d*$/;
      if (decimalPattern.test(value)) {
        // Store the string value temporarily for display
        setAddOnsData((prev) => ({
          ...prev,
          [id]: {
            ...prev[id],
            price: value // Store the string value
          }
        }));

        // Don't update addOnPrices yet - wait for valid number
        return;
      }
      return;
    }

    // Limit to max 5000
    if (numValue > 5000) {
      numValue = 5000;
      value = numValue.toString();
    }

    // Ensure minimum is not negative
    if (numValue < 0) {
      numValue = 0;
      value = numValue.toString();
    }

    // Update addOnsData with the price (store as string to preserve format)
    setAddOnsData((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        price: value // Store the original string value
      }
    }));

    // Update addOnPrices with the numeric value (keep as number for calculations)
    setAddOnPrices((prev) => ({
      ...prev,
      [id]: {
        price_per_item: numValue
      }
    }));
  };

  return (
    <>
      <Grid container spacing={2}>
        {addOnItems.map((item, index) => {
          const isChecked = checkedItems[index] || false;
          const currentValue = addOnsData[item.id]?.quantity || "";
          const priceValue = addOnsData[item.id]?.price || addOnPrices[item.id]?.price_per_item;

          return (
            <Grid item xs={12} sm={4} key={index}>
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isChecked}
                          onChange={() => handleCheck(index, item.id)}
                        />
                      }
                      label={
                        <Typography variant="body2" fontWeight={500}>
                          {item.name}
                        </Typography>
                      }
                    />

                    {isChecked && (
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 2,
                        mt: 2,
                        flexWrap: 'wrap'
                      }}>
                        {/* Quantity Field */}
                        <Box sx={{ minWidth: '80px' }}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              display: 'block',
                              mb: 0.5,
                              fontWeight: 500,
                              letterSpacing: '0.02em'
                            }}
                          >
                            Quantity
                          </Typography>
                          <TextField
                            size="small"
                            type="text"
                            value={currentValue}
                            onChange={(e) => {
                              const inputValue = e.target.value;

                              // Allow empty
                              if (inputValue === "") {
                                handleAddOnQuantityChange("", item.id, item.name);
                                return;
                              }

                              // Allow only numbers
                              const numberRegex = /^\d+$/;

                              if (numberRegex.test(inputValue)) {
                                const numValue = parseInt(inputValue, 10);
                                if (numValue <= 5000) {
                                  handleAddOnQuantityChange(inputValue, item.id, item.name);
                                }
                              }
                            }}
                            onBlur={(e) => {
                              const value = e.target.value;
                              if (value === "") {
                                handleAddOnQuantityChange("1", item.id, item.name);
                              }
                            }}
                            InputProps={{
                              inputProps: {
                                style: {
                                  textAlign: 'center',
                                  fontSize: '14px',
                                  padding: '8px 4px'
                                }
                              },
                            }}
                            sx={{ width: '80px' }}
                          />
                        </Box>

                        {/* Price Per Item Field */}
                        <Box sx={{ minWidth: '100px' }}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              display: 'block',
                              mb: 0.5,
                              fontWeight: 500,
                              letterSpacing: '0.02em'
                            }}
                          >
                            Price Per Item (SGD)
                          </Typography>
                          <TextField
                            size="small"
                            type="text"
                            value={priceValue || ''}
                            onChange={(e) => {
                              const inputValue = e.target.value;

                              // Allow empty
                              if (inputValue === "") {
                                handleAddOnPriceChange("", item.id);
                                return;
                              }
                              // Allow numbers with optional decimal
                              const decimalRegex = /^\d*\.?\d*$/;

                              if (decimalRegex.test(inputValue)) {
                                // Prevent multiple decimals
                                const decimalCount = (inputValue.match(/\./g) || []).length;
                                if (decimalCount > 1) return;

                                // Prevent multiple leading zeros
                                if (inputValue.length > 1 && inputValue.startsWith('0') && !inputValue.startsWith('0.')) {
                                  return;
                                }

                                handleAddOnPriceChange(inputValue, item.id);
                              }
                            }}
                            onBlur={(e) => {
                              const value = e.target.value;
                              if (value !== "" && !isNaN(parseFloat(value))) {
                                // Format to 2 decimal places on blur
                                const numValue = parseFloat(value);
                                handleAddOnPriceChange(numValue.toFixed(2), item.id);
                              }
                            }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start" sx={{ ml: 0.5 }}>
                                  S$
                                </InputAdornment>
                              ),
                              inputProps: {
                                style: {
                                  textAlign: "left",
                                  fontSize: "14px",
                                  padding: "8px 4px 8px 0"
                                }
                              }
                            }}
                            sx={{ width: "100px" }}
                          />
                        </Box>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {addOnItems.length === 0 && (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No add-on items available
          </Typography>
        </Box>
      )}
    </>
  );
};

// Main Catering Invoice Component
const CateringInvoiceComponent = ({ initialData, onSubmit, onCancel }) => {
  const [invoiceType, setInvoiceType] = useState('manual'); // 'manual', 'request', 'quotation'
  const { cateringOutlets } = useRestaurant()
  const [formData, setFormData] = useState({
    customerCompanyName: '',
    phoneNumber: '',
    email: '',
    address: '',
    eventDate: null,
    eventTime: null,
    eventDetails: '',
    eventName: '',
    termsConditions: '',
    foodCost: '',
    transportCost: '',
    buffetCost: '',
    discount: '',
    tax: '',
    menuSelections: [],
    notes: '',
    total_amount: 0,
    outlet_id: 0
  });

  const [menuModalOpen, setMenuModalOpen] = useState(false);
  const [editingMenuIndex, setEditingMenuIndex] = useState(null);
  const [mainMenuIds, setMainMenuIds] = useState([]);
  const [menuItemIds, setMenuItemIds] = useState({});
  const [price, setPrice] = useState({});
  const [quantity, setQuantity] = useState({});
  const [deliveryOption, setDeliveryOption] = useState("DELIVERY");
  const [selectedRequest, setSelectedRequest] = useState("");
  const [cateringRequests, setCateringRequests] = useState([]);
  const [mainMenus, setMainMenus] = useState([]);
  const [allMenuItems, setAllMenuItems] = useState([]);
  const [requestData, setRequestData] = useState(null);
  const [quotations, setQuotations] = useState([]);
  const [selectedQuotation, setSelectedQuotation] = useState("");
  const [quotationData, setQuotationData] = useState(null);
  const { showSuccess, showError } = useNotification();
  const [mode, setMode] = useState("create");
  const [choiceItems, setChoiceItems] = useState({});
  const [addOns, setAddons] = useState({})
  const [addOnPrices, setAddOnPrices] = useState({})
  const [addOnTotal, setAddOnTotal] = useState(0)
  const [eventTypeValue, setEventTypeValue] = useState("")

   const [isOutletInitialized, setIsOutletInitialized] = useState(false);


  const location = useLocation();
  const navigate = useNavigate();
  const invoiceId = location.state?.invoiceId || null;


  // useEffect(()=>{
  //   async function getInvoiceForEdit (){
  //     try{
  //       const response = await 
  //     }
  //     catch(error){

  //     }
  //   }
  //   if(invoiceId){
  //     getInvoiceForEdit()
  //   }
  // },[])
  // Load menu data on component mount

    const getOutletDataForNonSuperAdmins = async (outlet_id) => {
  
      try {
        const response = await outletService.getOutlet(outlet_id)
        // console.log("Outlet Response => ", response)
        let outlet_tax = response?.tax_config?.gst;
        setFormData({ ...formData, outlet_id:outlet_id,tax: outlet_tax });
      }
      catch (error) {
        // console.error("Error While Getting Outlet Change ", error);
      }
    }
  useEffect(() => {
      const initializeOutlet = async () => {
        const adminCheck = JSON.parse(localStorage.getItem("admin_outlet_id"));
  
        if (adminCheck && adminCheck !== 0) {
          // Admin user with specific outlet
          await getOutletDataForNonSuperAdmins(adminCheck);
        } else {
          // Super admin - no outlet restriction
          setIsOutletInitialized(true);
        }
      };
  
      initializeOutlet();
    }, []); // Empty dependency array - runs once on mount

  useEffect(() => {
    async function getMenuData() {
      try {
        const menusResponse = await cateringService.getMainMenus();
        const allMenuItemsResponse = await cateringService.getMenuItems();
        const requestsResponse = await cateringService.getCateringRequests();
        const quotationsResponse = await get_all_quotations();

        setMainMenus(menusResponse);
        setAllMenuItems(allMenuItemsResponse);
        setCateringRequests(requestsResponse);
        // console.log("Quotation Response => ", quotationsResponse)
        setQuotations(quotationsResponse.data || []);
      } catch (error) {
        // console.log("Error while getting data => ", error);
      }
    }
    getMenuData();

     let adminCheck = JSON.parse(localStorage.getItem("admin_outlet_id"))
    if (adminCheck) {
      setFormData((prev) => ({
        ...prev,
        outlet_id: adminCheck
      }));
      //  console.log("adminCheck => ", adminCheck)
      getOutletDataForNonSuperAdmins(adminCheck)

    }

  }, []);

  const handleInputChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value
    });
  };

  const handleNumberInput = (field) => (event) => {
    const value = event.target.value;

    if (value === '') {
      setFormData(prev => ({ ...prev, [field]: '' }));
      return;
    }


   

    const decimalRegex = /^-?\d*\.?\d*$/;

    if (decimalRegex.test(value)) {
      if (value === '.' || value === '-.' || value.startsWith('..')) {
        return;
      }

      const decimalCount = (value.match(/\./g) || []).length;
      if (decimalCount > 1) {
        return;
      }

      if (value.includes('-') && !value.startsWith('-')) {
        return;
      }

      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleNumberBlur = (field) => (event) => {
    const value = event.target.value;

    if (value === '' || value === '-' || value === '.' || value === '-.') {
      setFormData(prev => ({ ...prev, [field]: '' }));
    } else if (value.endsWith('.') || value.endsWith('.0')) {
      const numericValue = parseFloat(value);
      if (!isNaN(numericValue)) {
        setFormData(prev => ({ ...prev, [field]: numericValue.toString() }));
      }
    }
  };

  const handleDateChange = (field) => (date) => {
    setFormData({
      ...formData,
      [field]: date
    });
  };

  const handleTimeChange = (time) => {
    setFormData({
      ...formData,
      eventTime: time
    });
  };

  const handleInvoiceTypeChange = (event, newType) => {
    if (newType) {
      setInvoiceType(newType);

      // Reset selections when type changes
      resetForm();
    }
  };


  const handleOutletChange = async (e) => {
    // console.log("Outlet Value => ", e.target.value)
    try {
      const response = await outletService.getOutlet(e.target.value)
      // console.log("Outlet Response => ", response)
      let outlet_tax = response?.tax_config?.gst;
      setFormData({ ...formData, outlet_id: e.target.value, tax: outlet_tax });
    }
    catch (error) {
      // console.error("Error While Getting Outlet Change ", error);
    }

  }

  const handleRequestChange = async (e) => {
    setSelectedRequest(e.target.value);
    try {
      const response = await cateringService.getSpecificCateringRequest(e.target.value);
      // console.log("Request Response => ", response.data);
      setRequestData(response.data);

      let requestData = response.data
      let eventDate = null;
      let eventTime = null;

      if (response.data.event_date) {
        // Parse the date string (YYYY-MM-DD)
        eventDate = new Date(requestData.event_date);
      }

      if (response.data.event_time) {
        // Parse the time string (HH:MM:SS)
        const [hours, minutes, seconds] = requestData.event_time.split(':');
        eventTime = new Date();
        eventTime.setHours(parseInt(hours), parseInt(minutes), parseInt(seconds || 0));
      }

      setFormData(prev => ({
        ...prev,
        customerCompanyName: requestData.customer_name,
        menuSelections: requestData.menu_details,
        phoneNumber: requestData.customer_phone,
        eventDate: eventDate,
        eventTime: eventTime,
        email: requestData?.customer_email,
        eventName: requestData?.event_name,
        address: requestData?.venue_address,
        foodCost: requestData?.food_cost?.toString() || '',
        buffetCost: requestData?.buffet_cost?.toString() || '',
        transportCost: requestData?.transport_cost?.toString() || '',
        tax: requestData?.tax?.toString(),
        discount: requestData?.discount?.toString() || '',
        outlet_id: requestData?.outlet_id
      }));

      setEventTypeValue(requestData?.event_type.toLowerCase())
      setAddons(requestData.add_ons)
      setAddOnPrices(requestData.add_ons_price)
      setAddOnTotal(requestData.add_ons_total)

      if (response.data && response.data.menu_details) {
        const menu_details = response.data.menu_details;

        // Transform existing menu details
        const transformedMenuSelections = menu_details.map(menu => {
          const mainMenu = menu.main_menu;
          const transformedMenuItems = mainMenu.menu_items.map(item => ({
            id: item.id,
            name: item.name,
            description: item.description,
            fromCategory: mainMenu.id
          }));

          return {
            main_menu: {
              id: mainMenu.id,
              name: mainMenu.name,
              menuItems: transformedMenuItems,
              quantity: mainMenu.quantity || 1,
              price_per_person: mainMenu.price_per_person || 0,
              total: (mainMenu.quantity || 1) * (mainMenu.price_per_person || 0),
              method: 'request'
            }
          };
        });

        // REPLACE menu selections with request data (not merge)
        setFormData(prev => ({
          ...prev,
          menuSelections: transformedMenuSelections
        }));

        // Reset and set new main menu IDs
        const main_menu_ids = menu_details.map(menu => menu.main_menu.id);
        setMainMenuIds(main_menu_ids);

        // Reset and set new menu item IDs
        const menuItemIdsByMenuId = menu_details.reduce((acc, menu) => {
          const menuId = menu.main_menu.id;
          acc[menuId] = menu.main_menu.menu_items.map(item => item.id);
          return acc;
        }, {});
        setMenuItemIds(menuItemIdsByMenuId);

        // Reset and set new prices
        const priceObj = {};
        const quantityObj = {};

        menu_details.forEach(menu => {
          const menuId = menu.main_menu.id;
          priceObj[menuId] = menu.price || 0;
          quantityObj[menuId] = menu.quantity || 1;
        });

        setPrice(priceObj);
        setQuantity(quantityObj);

        // Reset choice items for this request
        setChoiceItems({});
      } else {
        // If no menu details, reset everything
        setFormData(prev => ({
          ...prev,
          menuSelections: []
        }));
        setMainMenuIds([]);
        setMenuItemIds({});
        setPrice({});
        setQuantity({});
        setChoiceItems({});
      }
    } catch (error) {
      // console.error("Error on Catering Request => ", error);
    }
  };

  const handleQuotationChange = async (e) => {
    setSelectedQuotation(e.target.value);
    try {
      const response = await get_specific_quotation(e.target.value);
      // console.log("Quotation Data => ", response);
      setQuotationData(response);
      setDeliveryOption(response.delivery_type)

      // Populate form with quotation data
      let eventDate = null;
      let eventTime = null;

      if (response.event_date) {
        eventDate = new Date(response.event_date);
      }

      if (response.event_time) {
        const [hours, minutes, seconds] = response.event_time.split(':');
        eventTime = new Date();
        eventTime.setHours(parseInt(hours), parseInt(minutes), parseInt(seconds || 0));
      }

      setFormData(prev => ({
        ...prev,
        customerCompanyName: response.customer_name,
        phoneNumber: response.customer_phone,
        eventDate: eventDate,
        eventTime: eventTime,
        eventName: response?.event_name,
        email: response?.customer_email,
        eventDetails:response?.event_details,
        termsConditions:response?.terms,
        address: response?.venue_address,
        foodCost: response?.food_cost?.toString() || '',
        buffetCost: response?.buffet_cost?.toString() || '',
        transportCost: response?.transport_cost?.toString() || '',
        tax: response?.tax?.toString(),
        discount: response?.discount?.toString() || '',
        outlet_id: response?.outlet_id
      }));

      setAddons(response.add_ons)
      setAddOnPrices(response.add_ons_price)
      setAddOnTotal(response.add_ons_total)

      setEventTypeValue(response?.event_type)


      // setEventTypeValue(response?.event_type.toLowerCase())

      // console.log("Menu Details Response => ", response.menu_details)

      // Transform menu selections from quotation
      if (response.menu_details && response.menu_details.length > 0) {
        const transformedMenuSelections = response.menu_details.map(menu => ({
          main_menu: {
            id: menu.main_menu.id,
            name: menu.main_menu.name,
            menuItems: menu.main_menu.menu_items,
            quantity: menu.main_menu.quantity || 1,
            price_per_person: menu.main_menu.price_per_person || 0,
            total: menu.main_menu.total || 0,
            method: 'quotation'
          }
        }));

        // REPLACE menu selections with quotation data (not merge)

        // console.log("Transformed Menu Selection => ", transformedMenuSelections)
        setFormData(prev => ({
          ...prev,
          menuSelections: transformedMenuSelections
        }));

        // Reset and set new main menu IDs
        const main_menu_ids = response.menu_details.map(menu => menu.main_menu.id);
        setMainMenuIds(main_menu_ids);

        // Reset and set new menu item IDs
        const menuItemIdsByMenuId = response.menu_details.reduce((acc, menu) => {
          const menuId = menu.main_menu.id;
          acc[menuId] = menu.main_menu.menu_items.map(item => item.id);
          return acc;
        }, {});
        setMenuItemIds(menuItemIdsByMenuId);

        // Reset and set new prices
        let updatedPrice = response?.menu_details.reduce((acc, menu) => {
          acc[menu.main_menu.id.toString()] = menu.main_menu.price_per_person;
          return acc;
        }, {});
        setPrice(updatedPrice);

        // Reset and set new quantities
        let updatedQuantity = response?.menu_details.reduce((acc, menu) => {
          acc[menu.main_menu.id.toString()] = menu.main_menu.quantity;
          return acc;
        }, {});
        setQuantity(updatedQuantity);

        // Reset and set new choice items
        let updatedChoiceItems = response?.menu_details.reduce((acc, menu) => {
          acc[menu.main_menu.id.toString()] = menu.main_menu.choice_items;
          return acc;
        }, {});
        setChoiceItems(updatedChoiceItems);
      } else {
        // If no menu details, reset menu-related states
        setFormData(prev => ({
          ...prev,
          menuSelections: []
        }));
        setMainMenuIds([]);
        setMenuItemIds({});
        setPrice({});
        setQuantity({});
        setChoiceItems({});
      }
    } catch (error) {
      // console.error("Error fetching quotation => ", error);
    }
  };


  const handlePhoneInputChange = (field, value) => {
    let phoneNumber_Length = value.target.countryValue.format.split('.').length - 1;
    // console.log("Phone Number Value => ", value)
    // setCountryNoLength(phoneNumber_Length)
    setFormData(prev => (prev ? {
      ...prev,
      phoneNumber: value.target.value
    } : null));


  };



  const handleMenuSave = (menuData) => {
    if (editingMenuIndex !== null) {
      // Update existing menu
      const updatedMenuSelections = [...formData.menuSelections];
      updatedMenuSelections[editingMenuIndex] = { main_menu: menuData };
      setFormData({ ...formData, menuSelections: updatedMenuSelections });
    } else {
      // Add new menu
      setFormData({
        ...formData,
        menuSelections: [...formData.menuSelections, { main_menu: menuData }]
      });
    }

    setMenuModalOpen(false);
    setEditingMenuIndex(null);
  };

  const handleEditMenu = (index) => {
    setEditingMenuIndex(index);
    setMenuModalOpen(true);
  };

  const handleRemoveMenu = (index) => {
    const menuToRemove = formData.menuSelections[index];
    const menuId = menuToRemove?.main_menu?.id || menuToRemove?.categoryId || menuToRemove?.id;

    if (menuId) {
      setMainMenuIds(prev => prev.filter(id => id !== menuId));
      setMenuItemIds(prevState => {
        const newState = { ...prevState };
        delete newState[menuId];
        return newState;
      });
      setPrice(prev => {
        const newState = { ...prev };
        delete newState[menuId];
        return newState;
      });
      setQuantity(prev => {
        const newState = { ...prev };
        delete newState[menuId];
        return newState;
      });
    }

    const updatedMenuSelections = formData.menuSelections.filter((_, i) => i !== index);
    setFormData({ ...formData, menuSelections: updatedMenuSelections });
  };

  const handleRadioChange = (event) => {
    if(event.target.value === "PICKUP"){
      setFormData(prev => ({ ...prev, ["transportCost"]: '',["buffetCost"]: '' }));
    }
    else if (event.target.value === "DELIVERY"){
      setFormData(prev => ({ ...prev,["buffetCost"]: '' }));
    }
    setDeliveryOption(event.target.value);

  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const menuTotal = formData.menuSelections.reduce((total, menu) => total + (menu.main_menu?.total || menu.total || 0), 0);

      const foodCost = formData.foodCost === '' || formData.foodCost === '.' || formData.foodCost === '-' ? 0 : parseFloat(formData.foodCost) || 0;
      const transportCost = formData.transportCost === '' || formData.transportCost === '.' || formData.transportCost === '-' ? 0 : parseFloat(formData.transportCost) || 0;
      const buffetCost = formData.buffetCost === '' || formData.buffetCost === '.' || formData.buffetCost === '-' ? 0 : parseFloat(formData.buffetCost) || 0;
      const discount = formData.discount === '' || formData.discount === '.' || formData.discount === '-' ? 0 : parseFloat(formData.discount) || 0;
      const tax = formData.tax === '' || formData.tax === '.' || formData.tax === '-' ? 0 : parseFloat(formData.tax) || 0;

      let subtotal = menuTotal + addOnTotal;
      const discountAmount = (subtotal * discount) / 100;
      const afterDiscount = subtotal - discountAmount;
      const taxAmount = (afterDiscount * tax) / 100;
      subtotal +=  foodCost + transportCost + buffetCost
      const totalAmount = afterDiscount + taxAmount +  foodCost + transportCost + buffetCost ;

      // console.log("subtotal => ", subtotal)
      // console.log("total Amount => ", totalAmount)

      const formatDate = (date) => {
        if (!date) return '';

        const d = new Date(date);

        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');

        return `${year}-${month}-${day}`;
      };

      const formatTime = (time) => {
        if (!time) return '';
        const t = new Date(time);
        return t.toTimeString().split(' ')[0];
      };

      if (formData.menuSelections.length === 0) {
        showError("Please Add atleast 1 Menu For Create a Invoice")
        return;
      }

      if (formData.customerCompanyName === "") {
        showError("Please Enter Customer Name / Company Name");
        return;
      }

      if (formData.customerCompanyName.length > 100) {
        showError("Please Enter Customer Name / Company Name less than 100 Characters");
        return;
      }

      if (!formData.eventDate) {
        showError("Please Select Event Date");
        return;
      }

      if (!formData.eventTime) {
        showError("Please Select Event Time");
        return;
      }

      if (formData.address && formData.address.length > 200) {
        showError("Please Enter Address Less than 200 Characters");
        return;
      }

      const uniqueMenuIds = [...new Set(mainMenuIds)];

      let invoice_payload_data = {
        "invoice_type": invoiceType,
        "outlet_id": formData.outlet_id,
        "customer_name": formData.customerCompanyName,
        "customer_phone": formData.phoneNumber,
        "customer_email": formData.email,
        "event_address": formData.address || "",
        "event_date": formatDate(formData.eventDate),
        "event_time": formatTime(formData.eventTime),
        "event_details": formData.eventDetails || "",
        "event_name": formData.eventName,
        "event_type": eventTypeValue,
        "terms": formData.termsConditions || "",
        "main_menu_ids": uniqueMenuIds,
        "menu_item_ids": menuItemIds,
        "price": price,
        "quantity": quantity,
        "food_cost": foodCost,
        "buffet_cost": buffetCost,
        "transport_cost": transportCost,
        "discount": discount.toString(),
        "tax": tax.toString(),
        "delivery_type": deliveryOption,
        "total_amount": totalAmount.toFixed(2),
        "request_id": selectedRequest === "" ? null : selectedRequest,
        "quotation_id": selectedQuotation === "" ? null : selectedQuotation,
        "choice_items": choiceItems,
        "add_ons": addOns,
        "add_ons_prices": addOnPrices,
        // "delivery_type": deliveryOption,
      };

      // console.log("Invoice Payload Data => ", invoice_payload_data);

      if (mode === 'create') {
        const response = await create_catering_invoice(invoice_payload_data);
        // console.log("Invoice saved:", response);
        showSuccess("Invoice Created Successfully");
        resetForm();
        navigate("/admin/info");
      } else {
        // const response = await update_catering_invoice(invoiceId, invoice_payload_data);
        // console.log("Invoice updated:", response);
        showSuccess("Invoice Updated Successfully");
        navigate("/admin/info");
      }

      if (onSubmit) {
        onSubmit({
          ...formData,
          menuTotal,
          subtotal,
          discountAmount,
          taxAmount,
          totalAmount,
          delivery_type: deliveryOption,
          invoice_type: invoiceType,
          status: formData.status
        });
      }
    } catch (error) {
      // console.error("Error submitting invoice:", error);
      showError(error.message || "Error submitting invoice");
    }
  };

  const resetForm = () => {
    setFormData(prev => ({
      ...prev,
      customerCompanyName: '',
      phoneNumber: '',
      email: '',
      address: '',
      eventDate: null,
      eventTime: null,
      eventDetails: '',
      termsConditions: '',
      foodCost: '',
      transportCost: '',
      buffetCost: '',
      discount: '',
      // tax: '0',
      menuSelections: [],
      notes: '',
      total_amount: 0,
      outlet_id: 0
    }));
    let adminCheck = JSON.parse(localStorage.getItem("admin_outlet_id"))
    if (adminCheck) {
      setFormData((prev) => ({
        ...prev,
        outlet_id: adminCheck
      }));
    }
    else {
      setFormData((prev) => ({
        ...prev,
        outlet_id: 0,
        // tax: 0,
      }));
    }
    setMainMenuIds([]);
    setMenuItemIds({});
    setPrice({});
    setQuantity({});
    setChoiceItems({});
    setSelectedRequest("");
    setSelectedQuotation("");
    setRequestData(null);
    setAddons({})
    setAddOnPrices({})
    setAddOnTotal(0)
    setQuotationData(null);
  };

  // Calculate totals
  const calculateTotals = () => {
    const menuTotal = formData.menuSelections.reduce((total, menu) => {
      const price = menu.main_menu?.total || menu.total || 0;
      const quantity = menu.main_menu?.quantity || menu.quantity || 1;

      return total + (price);
    }, 0);
    const foodCost = formData.foodCost === '' || formData.foodCost === '.' || formData.foodCost === '-' ? 0 : parseFloat(formData.foodCost) || 0;
    const transportCost = formData.transportCost === '' || formData.transportCost === '.' || formData.transportCost === '-' ? 0 : parseFloat(formData.transportCost) || 0;
    const buffetCost = formData.buffetCost === '' || formData.buffetCost === '.' || formData.buffetCost === '-' ? 0 : parseFloat(formData.buffetCost) || 0;
    const discount = formData.discount === '' || formData.discount === '.' || formData.discount === '-' ? 0 : parseFloat(formData.discount) || 0;
    const tax = formData.tax === '' || formData.tax === '.' || formData.tax === '-' ? 0 : parseFloat(formData.tax) || 0;

    let subtotal = menuTotal + addOnTotal;
    const discountAmount = (subtotal * discount) / 100;
    const afterDiscount = subtotal - discountAmount;
    const taxAmount = (afterDiscount * tax) / 100;
    const total =  afterDiscount + taxAmount + foodCost + transportCost + buffetCost;
    subtotal +=  foodCost + transportCost + buffetCost
    const additionaliCosts = foodCost + transportCost + buffetCost;

    return { menuTotal, subtotal, discountAmount, taxAmount, total, additionaliCosts };
  };

  const totals = calculateTotals();

  // Validation helpers
  const isFoodCostValid = formData.foodCost === '' || formData.foodCost === '.' || formData.foodCost === '-' || !isNaN(parseFloat(formData.foodCost));
  const isTransportCostValid = formData.transportCost === '' || formData.transportCost === '.' || formData.transportCost === '-' || !isNaN(parseFloat(formData.transportCost));
  const isBuffetCostValid = formData.buffetCost === '' || formData.buffetCost === '.' || formData.buffetCost === '-' || !isNaN(parseFloat(formData.buffetCost));
  const isDiscountValid = formData.discount === '' || formData.discount === '.' || formData.discount === '-' || !isNaN(parseFloat(formData.discount));
  const istaxValid = formData.tax === '' || formData.tax === '.' || formData.tax === '-' || !isNaN(parseFloat(formData.tax));

  // console.log("Form Data => ", formData)

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto' }}>
        {/* Header */}
        <Paper sx={{ p: 3, mb: 3, boxShadow: 2 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box>
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                {mode === 'create' ? 'Create New Invoice' : 'Edit Invoice'}
              </Typography>
            </Box>
            <Box>
              <Button
                startIcon={<CancelIcon />}
                onClick={() => navigate("/admin/info")}
                sx={{ mr: 1 }}
                variant="outlined"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveIcon />}
                form="invoice-form"
              >
                {mode === 'create' ? 'Create Invoice' : 'Update Invoice'}
              </Button>
            </Box>
          </Box>

          {/* Invoice Type Selection - Always show in create mode */}
          {mode === 'create' && (
            <Box mb={3}>
              <Typography variant="subtitle2" gutterBottom fontWeight="medium">
                Invoice Type
              </Typography>
              <ToggleButtonGroup
                value={invoiceType}
                exclusive
                onChange={handleInvoiceTypeChange}
                aria-label="invoice type"
                size="small"
                sx={{ mb: 2 }}
              >
                <ToggleButton value="manual" aria-label="manual">
                  Manual
                </ToggleButton>
                <ToggleButton value="request" aria-label="request">
                  Request Based
                </ToggleButton>
                <ToggleButton value="quotation" aria-label="quotation">
                  From Quotation
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          )}

          {/* Type-specific selection */}
          {invoiceType === "request" && mode === 'create' && (
            <Box mb={3} sx={{ ml: 3 }}>
              <FormControl fullWidth size="small" sx={{ maxWidth: 400, mt: 1 }}>
                <InputLabel>Select Request Number</InputLabel>
                <Select
                  value={selectedRequest}
                  onChange={handleRequestChange}
                  label="Select Request Number"
                >
                  <MenuItem value="" disabled>Select Request Number</MenuItem>
                  {cateringRequests.map((request) => (
                    <MenuItem key={request.id} value={request.id}>
                      {request.request_number} <Chip sx={{ ml: 1, mr: 2 }} label={request.customer_name}></Chip> {request.customer_phone}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}

          {invoiceType === "quotation" && mode === 'create' && (
            <Box mb={3} sx={{ ml: 3 }}>
              <FormControl fullWidth size="small" sx={{ maxWidth: 400, mt: 1 }}>
                <InputLabel>Select Quotation</InputLabel>
                <Select
                  value={selectedQuotation}
                  onChange={handleQuotationChange}
                  label="Select Quotation"
                >
                  <MenuItem value="" disabled>Select Quotation</MenuItem>
                  {quotations.map((quotation) => (
                    <MenuItem key={quotation.id} value={quotation.id}>
                      {quotation.quotation_number} <Chip
                        sx={{ ml: 1, mr: 2 }}
                        label={quotation.customer_name}
                      />
                      {quotation.customer_phone}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}

          <Box sx={{ ml: 3, width: '35%' }}>
            <FormControl fullWidth size="small">
              <Select
                value={formData.outlet_id}
                onChange={handleOutletChange}
                displayEmpty
                disabled={JSON.parse(localStorage.getItem('admin_outlet_id')) ? true : false}
                sx={{ backgroundColor: 'background.paper' }}
                onWheel={(e) => e.stopPropagation()}
                onOpen={() => {
                  document.body.style.overflow = 'hidden';
                  document.documentElement.style.overflow = 'hidden';
                }}
                onClose={() => {
                  document.body.style.overflow = '';
                  document.documentElement.style.overflow = '';
                }}
                MenuProps={{
                  disableScrollLock: true,
                }}
              >
                <MenuItem value={0} disabled>Select Outlet</MenuItem>
                {cateringOutlets.map((outlet) => (
                  <MenuItem key={outlet.id} value={outlet.id}>
                    {outlet.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <form id="invoice-form" onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              {/* Left Column - Customer & Event Details */}
              <Grid item xs={12} md={6}>
                <Paper elevation={0} sx={{ p: 3, height: '100%' }}>
                  <Typography variant="h6" gutterBottom color="primary" fontWeight="medium">
                    Invoice & Customer Details
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Customer / Company Name"
                        value={formData.customerCompanyName}
                        onChange={handleInputChange('customerCompanyName')}
                        size="small"
                        placeholder="John Doe / Acme Corporation"

                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <PhoneInput
                        country="sg"
                        specialLabel=""
                        value={formData?.phoneNumber || ''}
                        onChange={(phone, country) => handlePhoneInputChange("phone", { target: { value: phone, countryValue: country } })}
                        inputStyle={{
                          width: '100%',
                          height: '32.25px',
                          borderRadius: '7px',
                          color: 'black',
                          paddingLeft: '58px',
                          fontSize: '12px !important',
                        }}
                        buttonStyle={{
                          borderTopLeftRadius: '7px',
                          borderBottomLeftRadius: '7px',
                        }}
                        containerStyle={{
                          width: '100%',
                        }}
                      />
                      {/* <TextField
                        fullWidth
                        label="Phone Number"
                        value={formData.phoneNumber}
                        onChange={handleInputChange('phoneNumber')}
                        size="small"
                        
                      /> */}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange('email')}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Address"
                        multiline
                        rows={2}
                        value={formData.address}
                        onChange={handleInputChange('address')}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <DatePicker
                        label="Event Date"
                        value={formData.eventDate}
                        onChange={handleDateChange('eventDate')}
                        format="dd/MM/yyyy"
                        slotProps={{
                          textField: { fullWidth: true, size: "medium", },
                        }}
                        onOpen={() => {
                          document.body.style.overflow = 'hidden';
                          document.documentElement.style.overflow = 'hidden';
                        }}
                        onClose={() => {
                          document.body.style.overflow = '';
                          document.documentElement.style.overflow = '';
                        }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TimePicker
                        label="Event Time"
                        value={formData.eventTime}
                        onChange={handleTimeChange}
                        onOpen={() => {
                          document.body.style.overflow = 'hidden';
                          document.documentElement.style.overflow = 'hidden';
                        }}
                        onClose={() => {
                          document.body.style.overflow = '';
                          document.documentElement.style.overflow = '';
                        }}
                        renderInput={(params) => <TextField {...params} fullWidth size="small" />}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Event Name"
                        type="text"
                        value={formData.eventName}
                        onChange={handleInputChange('eventName')}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <FormControl fullWidth size="small">
                        <Select
                          value={eventTypeValue}
                          onChange={(e) => setEventTypeValue(e.target.value)}
                          displayEmpty
                          sx={{ backgroundColor: 'background.paper' }}
                          onWheel={(e) => e.stopPropagation()}
                          onOpen={() => {
                            document.body.style.overflow = 'hidden';
                            document.documentElement.style.overflow = 'hidden';
                          }}
                          onClose={() => {
                            document.body.style.overflow = '';
                            document.documentElement.style.overflow = '';
                          }}
                          MenuProps={{
                            disableScrollLock: true,
                          }}
                        >
                          <MenuItem value="" disabled>Select Event Type</MenuItem>
                          <MenuItem value="wedding" >Wedding</MenuItem>
                          <MenuItem value="birthday" >Birthday</MenuItem>
                          <MenuItem value="corporate" >Corporate</MenuItem>
                          <MenuItem value="anniversary">Anniversary</MenuItem>
                          <MenuItem value="festival">Festival</MenuItem>
                          <MenuItem value="other">Other</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Event Details"
                        multiline
                        rows={3}
                        value={formData.eventDetails}
                        onChange={handleInputChange('eventDetails')}
                        placeholder="Describe the event, theme, special requirements, etc."
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Terms & Conditions"
                        multiline
                        rows={3}
                        value={formData.termsConditions}
                        onChange={handleInputChange('termsConditions')}
                        placeholder="Payment terms, cancellation policy, special conditions..."
                        size="small"
                      />
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              {/* Right Column - Cost Calculation */}
              <Grid item xs={12} md={6}>
                <Paper elevation={0} sx={{ p: 3, height: '100%' }}>
                  <Typography variant="h6" gutterBottom color="primary" fontWeight="medium">
                    Cost Calculation
                  </Typography>

                  <FormControl sx={{ mb: 2 }}>
                    <RadioGroup
                      row
                      aria-labelledby="demo-row-radio-buttons-group-label"
                      name="row-radio-buttons-group"
                      value={deliveryOption}
                      onChange={handleRadioChange}
                    >
                      <FormControlLabel value="DELIVERY" control={<Radio />} label="Delivery" />
                      <FormControlLabel value="BUFFET" control={<Radio />} label="Buffet" />
                      <FormControlLabel value="PICKUP" control={<Radio />} label="Pickup" />
                    </RadioGroup>
                  </FormControl>

                  <Stack spacing={2}>
                    {/* <TextField
                      label="Food Cost"
                      type="text"
                      value={formData.foodCost}
                      onChange={handleNumberInput('foodCost')}
                      onBlur={handleNumberBlur('foodCost')}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      }}
                      fullWidth
                      size="small"
                      error={!isFoodCostValid}
                      helperText={!isFoodCostValid ? "Please enter a valid number" : ""}
                    /> */}

                    {deliveryOption !== "PICKUP" &&
                      <TextField
                        label="Transport Cost"
                        type="text"
                        value={formData.transportCost}
                        onChange={handleNumberInput('transportCost')}
                        onBlur={handleNumberBlur('transportCost')}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">S$</InputAdornment>,
                        }}
                        fullWidth
                        size="small"
                        error={!isTransportCostValid}
                        helperText={!isTransportCostValid ? "Please enter a valid number" : ""}
                      />
                    }

                    {deliveryOption === "BUFFET" &&
                      <TextField
                        label="Buffet Cost"
                        type="text"
                        value={formData.buffetCost}
                        onChange={handleNumberInput('buffetCost')}
                        onBlur={handleNumberBlur('buffetCost')}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">S$</InputAdornment>,
                        }}
                        fullWidth
                        size="small"
                        error={!isBuffetCostValid}
                        helperText={!isBuffetCostValid ? "Please enter a valid number" : ""}
                      />}

                    <TextField
                      label="Discount"
                      type="text"
                      value={formData.discount}
                      onChange={handleNumberInput('discount')}
                      onBlur={handleNumberBlur('discount')}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">%</InputAdornment>,
                      }}
                      fullWidth
                      size="small"
                      error={!isDiscountValid}
                      helperText={!isDiscountValid ? "Please enter a valid number" : ""}
                    />

                    <TextField
                      label="Tax"
                      type="text"
                      value={formData.tax}
                      onChange={handleNumberInput('tax')}
                      onBlur={handleNumberBlur('tax')}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">%</InputAdornment>,
                      }}
                      fullWidth
                      size="small"
                      error={!istaxValid}
                      helperText={!istaxValid ? "Please enter a valid number" : ""}
                    />

                    <Divider sx={{ my: 2 }} />

                    {/* Totals Display */}
                    <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, border: 1, borderColor: 'divider' }}>
                      <Stack spacing={1.5}>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2">Menu Items Total:</Typography>
                          <Typography variant="body2" fontWeight="medium">
                            S${totals.menuTotal.toFixed(2)}
                          </Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2">Add Ons Total:</Typography>
                          <Typography variant="body2" fontWeight="medium">
                            S${addOnTotal.toFixed(2)}
                          </Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2">Additional Costs:</Typography>
                          <Typography variant="body2" fontWeight="medium">
                            S${totals.additionaliCosts.toFixed(2)}
                          </Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2">Subtotal:</Typography>
                          <Typography variant="body2" fontWeight="medium">
                            S${totals.subtotal.toFixed(2)}
                          </Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2">Discount ({parseFloat(formData.discount) || 0}%):</Typography>
                          <Typography variant="body2" color="error" fontWeight="medium">
                            -S${totals.discountAmount.toFixed(2)}
                          </Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2">tax ({parseFloat(formData.tax)}%):</Typography>
                          <Typography variant="body2" fontWeight="medium">
                            S${totals.taxAmount.toFixed(2)}
                          </Typography>
                        </Box>
                        <Divider />
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography variant="h6">Total Amount:</Typography>
                          <Typography variant="h6" color="primary" fontWeight="bold">
                            S${totals.total.toFixed(2)}
                          </Typography>
                        </Box>
                      </Stack>
                    </Box>
                  </Stack>
                </Paper>
              </Grid>
            </Grid>
          </form>
        </Paper>

        {/* Menu Selections Section */}
        <Paper sx={{ p: 3, mb: 3, boxShadow: 2, border: 1, borderColor: 'divider' }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Box>
              <Typography variant="h6" color="primary" fontWeight="medium" gutterBottom>
                Menu Selections
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Add multiple menu selections
              </Typography>
            </Box>

            {/* Always show Add button in create mode for all types */}
            {mode === 'create' && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                  setEditingMenuIndex(null);
                  setMenuModalOpen(true);
                }}
              >
                Add Menu Selection
              </Button>
            )}
          </Box>

          {/* Menu Selections Table */}
          {formData.menuSelections.length === 0 ? (
            <Alert severity="info" sx={{ mb: 3 }}>
              No menu selections added.
            </Alert>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell><Typography variant="subtitle2" fontWeight="bold">Source</Typography></TableCell>
                    <TableCell><Typography variant="subtitle2" fontWeight="bold">Category</Typography></TableCell>
                    <TableCell><Typography variant="subtitle2" fontWeight="bold">Quantity</Typography></TableCell>
                    <TableCell><Typography variant="subtitle2" fontWeight="bold">Price per Item</Typography></TableCell>
                    <TableCell><Typography variant="subtitle2" fontWeight="bold">Total</Typography></TableCell>
                    {mode === 'create' && <TableCell align="center"><Typography variant="subtitle2" fontWeight="bold">Actions</Typography></TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {formData.menuSelections.map((menu, index) => (
                    <TableRow
                      key={`${menu.main_menu?.id || menu.id}-${index}`}
                      hover
                      sx={{
                        '&:last-child td, &:last-child th': { border: 0 },
                        '&:hover': { bgcolor: 'action.hover' }
                      }}
                    >
                      <TableCell>
                        <Chip
                          label={menu.main_menu?.method || 'manual'}
                          size="small"
                          color={menu.main_menu?.method === 'manual' ? 'primary' : 'secondary'}
                          variant="outlined"
                          sx={{ fontWeight: 'medium' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {menu?.main_menu?.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {menu?.main_menu?.quantity}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          S${menu?.main_menu?.price_per_person}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold" color="primary">
                          S${menu?.main_menu?.total}
                        </Typography>
                      </TableCell>
                      {mode === 'create' && (
                        <TableCell align="center">
                          <Stack direction="row" spacing={1} justifyContent="center">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleEditMenu(index)}
                              title="Edit"
                              sx={{
                                border: 1,
                                borderColor: 'primary.main',
                                '&:hover': { bgcolor: 'primary.light' }
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleRemoveMenu(index)}
                              title="Remove"
                              sx={{
                                border: 1,
                                borderColor: 'error.main',
                                '&:hover': { bgcolor: 'error.light' }
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Stack>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {formData.menuSelections.length > 0 && (
            <Box mt={2} pt={2} borderTop={1} borderColor="divider">
              <Typography variant="body2" color="text.secondary">
                Total Menu Selections: {formData.menuSelections.length} |
                Total Menu Amount: S${totals.menuTotal.toFixed(2)}
              </Typography>
            </Box>
          )}
        </Paper>

        {/* Notes Section */}
        {/* <Paper sx={{ p: 3, boxShadow: 2, border: 1, borderColor: 'divider' }}>
          <Typography variant="h6" gutterBottom color="primary" fontWeight="medium">
            Additional Notes
          </Typography>
          <TextField
            fullWidth
            label="Notes"
            multiline
            rows={3}
            value={formData.notes}
            onChange={handleInputChange('notes')}
            placeholder="Any additional notes or special instructions..."
          />
        </Paper> */}


        <Paper sx={{ p: 3, mb: 3, boxShadow: 2, border: 1, borderColor: 'divider' }}>
          {/* <Typography>Hello World</Typography> */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Box>
              <Typography variant="h6" color="primary" fontWeight="medium" gutterBottom>
                Add Ons Selections
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Add Mutilple Add ons Here
              </Typography>
            </Box>
          </Box>
          <AddonAdditionComponent
            addOnsData={addOns}
            setAddOnsData={setAddons}
            addOnPrices={addOnPrices}
            setAddOnPrices={setAddOnPrices}
            addOnTotal={addOnTotal}
            setAddOnTotal={setAddOnTotal}

          />
        </Paper>
      </Box>

      {/* Menu Selection Modal */}
      {mode === 'create' && (
        <MenuSelectionModal
          open={menuModalOpen}
          onClose={() => {
            setMenuModalOpen(false);
            setEditingMenuIndex(null);
          }}
          onSave={handleMenuSave}
          initialData={editingMenuIndex !== null ? formData.menuSelections[editingMenuIndex] : null}
          mainMenuIds={mainMenuIds}
          setMainMenuIds={setMainMenuIds}
          menuItemIds={menuItemIds}
          setMenuItemIds={setMenuItemIds}
          setMenuPrice={setPrice}
          setMenuQuantity={setQuantity}
          isEditing={editingMenuIndex !== null}
          editingMenuId={editingMenuIndex !== null ?
            formData.menuSelections[editingMenuIndex]?.main_menu?.id ||
            formData.menuSelections[editingMenuIndex]?.id : null}
          allMenuItems={allMenuItems}
          mainMenus={mainMenus}
          choiceItems={choiceItems}
          setChoiceItems={setChoiceItems}
          isViewOnly={false}
        />
      )}
    </LocalizationProvider>
  );
};

export default CateringInvoiceComponent;